import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { storeApi } from "@/lib/storefront";

const RV_KEY = "store_recently_viewed_v1";
const MAX = 12;
// Throttle: don't re-check more than once per 30s — admin won't delete that often
const PRUNE_THROTTLE_MS = 30_000;

const RecentlyViewedContext = createContext(null);

function read() {
  try { const raw = localStorage.getItem(RV_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
}
function write(v) { try { localStorage.setItem(RV_KEY, JSON.stringify(v)); } catch { /* noop */ } }

export function RecentlyViewedProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return typeof window !== "undefined" ? read() : []; } catch { return []; }
  });
  const location = useLocation();
  const lastPruneRef = useRef(0);

  useEffect(() => { write(items); }, [items]);

  // Prune dead products via backend check-alive. Throttled to once per 30s so
  // fast SPA navigation doesn't spam the endpoint.
  const prune = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastPruneRef.current < PRUNE_THROTTLE_MS) return;
    const current = read();
    if (current.length === 0) return;
    try {
      const ids = current.map((p) => p.id).filter(Boolean);
      const { data } = await storeApi.post("/products/check-alive", { ids });
      const alive = new Set(data?.alive || []);
      const pruned = current.filter((p) => alive.has(p.id));
      lastPruneRef.current = now;
      if (pruned.length !== current.length) {
        setItems(pruned);
      }
    } catch {
      // Network hiccup — keep what we have, retry on next opportunity
    }
  }, []);

  // 1) Prune on first mount.
  useEffect(() => { prune(true); }, [prune]);

  // 2) Prune on every route change (SPA navigation).
  useEffect(() => { prune(); }, [location.pathname, prune]);

  // 3) Prune when tab regains focus / becomes visible (long-running tab).
  useEffect(() => {
    const onFocus = () => prune();
    const onVisible = () => { if (document.visibilityState === "visible") prune(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [prune]);

  const track = useCallback((product) => {
    if (!product?.id) return;
    setItems((prev) => {
      const filtered = prev.filter((x) => x.id !== product.id);
      const slim = {
        id: product.id,
        title: product.title,
        primary_image: product.primary_image || product.images?.[0] || "",
        price: Number(product.price || 0),
        currency: product.currency || "USD",
        old_price: product.old_price,
        seo_slug: product.seo_slug || product.id,
        stock: product.stock,
      };
      return [slim, ...filtered].slice(0, MAX);
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return (
    <RecentlyViewedContext.Provider value={{ items, track, clear, count: items.length }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewed() {
  const ctx = useContext(RecentlyViewedContext);
  if (!ctx) throw new Error("useRecentlyViewed must be inside RecentlyViewedProvider");
  return ctx;
}
